import React, { useState, useEffect } from 'react';
import SectionOverview from './SectionOverview';
import FontSettings from './FontSettings';
import Swal from 'sweetalert2';

// For static assets, use ASSET_URL in dev, hostname in prod; fallback to API origin so uploads load
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

function getAssetBaseUrl(api) {
  if (ASSET_BASE_URL) return ASSET_BASE_URL;
  if (api?.defaults?.baseURL) return api.defaults.baseURL.replace(/\/api\/?$/, '');
  return '';
}

const ThemesAndLayout = ({ 
  campaign, 
  theme, 
  sections = [],
  winnerSections = [],
  comingSoonSections = [],
  uploadingLogo,
  uploadingBackground,
  onThemeUpdate,
  onSectionsChange,
  onWinnerSectionsChange,
  onComingSoonSectionsChange,
  onSave,
  onLogoUpload,
  onRemoveLogo,
  onBackgroundUpload,
  onRemoveBackground,
  campaignId,
  api,
  onAddSection,
  onAddWinnerSection,
  onAddComingSoonSection,
  onDeleteSection,
  onDeleteWinnerSection,
  onDeleteComingSoonSection,
  onDuplicateSection,
  onDuplicateWinnerSection,
  onDuplicateComingSoonSection,
  onApplyTemplate,
  setSuccessMessage,
  setShowSuccess,
  setErrorMessage,
  setShowError
}) => {
  const [activeTab, setActiveTab] = useState('landing');
  const [isSaving, setIsSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  const hasNoSections = sections.length === 0 && winnerSections.length === 0 && comingSoonSections.length === 0;

  useEffect(() => {
    if (hasNoSections && api) {
      api.get('/admin/templates')
        .then(res => setTemplates(res.data.templates || []))
        .catch(err => console.error('Failed to fetch templates:', err));
    }
  }, [hasNoSections, api]);

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId || !campaignId) return;

    const result = await Swal.fire({
      title: 'Apply Template?',
      text: 'This will set the theme, fonts, and all page sections from the selected template.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0f766e',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, apply it',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    setApplyingTemplate(true);
    try {
      const response = await api.post(`/admin/campaigns/${campaignId}/apply-template`, {
        template_id: selectedTemplateId
      });
      if (onApplyTemplate) {
        onApplyTemplate(response.data);
      }
      if (setSuccessMessage && setShowSuccess) {
        setSuccessMessage('Template applied successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Failed to apply template:', err);
      if (setErrorMessage && setShowError) {
        setErrorMessage(err.response?.data?.error || 'Failed to apply template');
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      }
    } finally {
      setApplyingTemplate(false);
    }
  };

  const handleThemeUpdate = (field, value) => {
    onThemeUpdate(field, value);
  };
  
  // Function to handle tab change and reset any editing state
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // You might need to reset any editing state in SectionOverview
    // This will cause SectionOverview to re-render with new sections
  };

  const handleSaveAll = async () => {
    if (isSaving) return; // Prevent multiple simultaneous saves
    
    setIsSaving(true);
    try {
      // Save theme only (sections are saved individually on their dedicated pages)
      await onSave();
    } catch (error) {
      console.error('Failed to save:', error);
      // Error handling is done in the parent component
      throw error;
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Template Selector - only when no sections exist */}
      {hasNoSections && templates.length > 0 && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-teal-900 mb-2">Start from a Template</h3>
          <p className="text-sm text-teal-700 mb-4">
            Choose a template to pre-fill the theme, fonts, and page sections for this campaign.
          </p>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-4 py-3 border border-teal-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white text-gray-700"
              >
                <option value="">Select a template...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleApplyTemplate}
              disabled={!selectedTemplateId || applyingTemplate}
              className="px-6 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium whitespace-nowrap"
            >
              {applyingTemplate ? 'Applying...' : 'Apply Template'}
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('landing')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'landing'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Landing Page
          </button>
          <button
            onClick={() => handleTabChange('winner')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'winner'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Comp close page
          </button>
          <button
            onClick={() => handleTabChange('comingSoon')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'comingSoon'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Coming soon page
          </button>
          <button
            onClick={() => handleTabChange('fonts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'fonts'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Fonts
          </button>
          <button
            onClick={() => handleTabChange('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Themes
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'landing' ? (
        <div>
          <SectionOverview
            key="landing-sections"
            campaignId={campaignId}
            sections={sections}
            pageType="landing_page"
            onAddSection={onAddSection}
            onDeleteSection={onDeleteSection}
            onDuplicateSection={onDuplicateSection}
            onUpdateSection={onSectionsChange}
            api={api}
          />
        </div>
      ) : activeTab === 'winner' ? (
        <div>
          <SectionOverview
            key="winner-sections"
            campaignId={campaignId}
            sections={winnerSections}
            pageType="winner_page"
            onAddSection={onAddWinnerSection}
            onDeleteSection={onDeleteWinnerSection}
            onDuplicateSection={onDuplicateWinnerSection}
            onUpdateSection={onWinnerSectionsChange}
            api={api}
            isWinnerPage={true}
          />
        </div>
      ) : activeTab === 'comingSoon' ? (
        <div>
          <SectionOverview
            key="coming-soon-sections"
            campaignId={campaignId}
            sections={comingSoonSections}
            pageType="coming_soon_page"
            onAddSection={onAddComingSoonSection}
            onDeleteSection={onDeleteComingSoonSection}
            onDuplicateSection={onDuplicateComingSoonSection}
            onUpdateSection={onComingSoonSectionsChange}
            api={api}
          />
        </div>
      ) : activeTab === 'fonts' ? (
        <div>
          <FontSettings
            theme={theme}
            onThemeUpdate={onThemeUpdate}
            campaignId={campaignId}
            api={api}
            setSuccessMessage={setSuccessMessage}
            setShowSuccess={setShowSuccess}
            setErrorMessage={setErrorMessage}
            setShowError={setShowError}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Theme Colors */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Theme Colors</h3>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                <p className="text-xs text-gray-500 mb-2">Used for headings and main titles</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={theme.primary_color}
                    onChange={(e) => handleThemeUpdate('primary_color', e.target.value)}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.primary_color}
                    onChange={(e) => handleThemeUpdate('primary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                <p className="text-xs text-gray-500 mb-2">Used for paragraphs and labels</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={theme.secondary_color}
                    onChange={(e) => handleThemeUpdate('secondary_color', e.target.value)}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.secondary_color}
                    onChange={(e) => handleThemeUpdate('secondary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
                <p className="text-xs text-gray-500 mb-2">Used for buttons and call-to-action elements</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={theme.accent_color}
                    onChange={(e) => handleThemeUpdate('accent_color', e.target.value)}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.accent_color}
                    onChange={(e) => handleThemeUpdate('accent_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Campaign Logo */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Logo</h3>
            {theme.logo_url && (
              <img 
                src={`${getAssetBaseUrl(api)}${theme.logo_url}`}
                alt="Campaign logo" 
                className="h-20 object-contain mb-4"
              />
            )}
            <div className="flex items-center space-x-4">
              <label className="cursor-pointer px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                <span>{uploadingLogo ? 'Uploading...' : 'Upload Logo'}</span>
                <input
                  type="file"
                  className="sr-only"
                  accept="image/*"
                  onChange={onLogoUpload}
                  disabled={uploadingLogo}
                />
              </label>
              {theme.logo_url && (
                <button
                  type="button"
                  onClick={() => onRemoveLogo ? onRemoveLogo() : handleThemeUpdate('logo_url', '')}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="mt-4">
              <h4 className="text-base font-medium text-gray-900 mb-3">Background Image</h4>
              {theme.background_image_url && (
                <img
                  src={`${getAssetBaseUrl(api)}${theme.background_image_url}`}
                  alt="Campaign background"
                  className="h-20 object-contain mb-4"
                />
              )}
              <div className="flex items-center space-x-4">
                <label className="cursor-pointer px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                  <span>{uploadingBackground ? 'Uploading...' : 'Upload Background'}</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={onBackgroundUpload}
                    disabled={uploadingBackground}
                  />
                </label>
                {theme.background_image_url && (
                  <button
                    type="button"
                    onClick={() => onRemoveBackground ? onRemoveBackground() : handleThemeUpdate('background_image_url', '')}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Thank You Message */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Thank You Message</h3>
            <p className="text-xs text-gray-500 mb-2">
              Default copy after entry. Plain text or HTML.
            </p>
            <textarea
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-y min-h-[120px]"
              value={theme.thank_you_message ?? ''}
              onChange={(e) => handleThemeUpdate('thank_you_message', e.target.value)}
              placeholder="Enter thank you message..."
            />
          </div>

          {/* Instant win / comp outcomes (thank-you page copy) */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Winner message</h3>
            <p className="text-xs text-gray-500 mb-2">
              Shown when an entrant wins (e.g. instant win). Plain text or HTML.
            </p>
            <textarea
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-y min-h-[120px]"
              value={theme.winner_message ?? ''}
              onChange={(e) => handleThemeUpdate('winner_message', e.target.value)}
              placeholder="Message for winners..."
            />
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Non-winner message</h3>
            <p className="text-xs text-gray-500 mb-2">
              Shown when an entrant does not win. Plain text or HTML.
            </p>
            <textarea
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-y min-h-[120px]"
              value={theme.loser_message ?? ''}
              onChange={(e) => handleThemeUpdate('loser_message', e.target.value)}
              placeholder="Message for non-winners..."
            />
          </div>
        </div>
      )}

      {/* Save Button - Only visible when on Themes tab */}
      {activeTab === 'settings' && (
        <div className="border-t pt-6">
          <button
            type="button"
            onClick={handleSaveAll}
            disabled={isSaving}
            className={`px-6 py-3 rounded-xl font-medium flex items-center space-x-2 ${
              isSaving 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-teal-700 hover:bg-teal-600 cursor-pointer'
            } text-white`}
          >
            {isSaving && (
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <span>{isSaving ? 'Saving...' : 'Save Theme'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ThemesAndLayout;
